import {
  Component,
  inject,
  OnInit,
  OnDestroy,
  HostListener,
  ViewChild,
  ElementRef,
  NgZone,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { AchievementsService } from '../../services/achievements.service';
import { AuthService } from '../../../auth/services/auth.service';
import { PermissionCode } from '../../../../shared/enums/permission-code';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { SkeletonComponent } from '../../../../shared/components/skeleton/skeleton.component';
import {
  AchievementsConnectionOverviewResponse,
  AchievementOverviewItem,
  AchievementProgress,
  AchievementConnection,
  Achievement,
  EventType,
} from '../../models/achievements';
import {
  ConfettiParticle,
  BadgeTierInfo,
  BADGE_TIERS,
  AchievementSection,
  EVENT_TYPE_LABELS,
  EVENT_TYPE_DESCRIPTIONS,
  SECTION_THEMES,
  RECENT_THRESHOLD_DAYS,
} from '../../models/achievement-badges';

@Component({
  selector: 'app-achievements',
  standalone: true,
  imports: [CommonModule, SvgIconComponent, SkeletonComponent],
  templateUrl: './achievements.component.html',
  styleUrl: './achievements.component.scss',
})
export class AchievementsComponent
  extends MenuComponentBase
  implements OnInit, OnDestroy
{
  @ViewChild('confettiCanvas')
  confettiCanvasRef!: ElementRef<HTMLCanvasElement>;

  private achievementsService = inject(AchievementsService);
  private authService = inject(AuthService);
  private ngZone = inject(NgZone);
  private router = inject(Router);

  loading = true;
  error = false;
  errorMessage = '';

  overview: AchievementsConnectionOverviewResponse | null = null;
  sections: AchievementSection[] = [];
  recentlyUnlocked: AchievementConnection[] = [];
  recentlyUnlockedIds = new Set<number>();
  achievementTierMap = new Map<number, BadgeTierInfo>();

  selectedAchievement: AchievementOverviewItem | null = null;
  selectedSection: AchievementSection | null = null;

  celebratingAchievementId: number | null = null;
  activeRecentAchievementId: number | null = null;
  private celebratingTimeout: ReturnType<typeof setTimeout> | null = null;
  private scrollTimeout: ReturnType<typeof setTimeout> | null = null;
  private activeParticles: ConfettiParticle[] = [];
  private animationFrameId: number | null = null;

  readonly EVENT_TYPE_LABELS = EVENT_TYPE_LABELS;
  readonly EVENT_TYPE_DESCRIPTIONS = EVENT_TYPE_DESCRIPTIONS;
  readonly SECTION_THEMES = SECTION_THEMES;
  readonly RECENT_THRESHOLD_DAYS = RECENT_THRESHOLD_DAYS;

  get totalEarned(): number {
    return this.sections.reduce(
      (sum, s) => sum + s.achievements.filter((a) => a.earned).length,
      0,
    );
  }

  get totalAvailable(): number {
    return this.sections.reduce((sum, s) => sum + s.achievements.length, 0);
  }

  get overallProgressPercent(): number {
    if (this.totalAvailable === 0) return 0;
    return Math.round((this.totalEarned / this.totalAvailable) * 100);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadAchievements();
  }

  loadAchievements(): void {
    this.loading = true;
    this.error = false;

    this.achievementsService
      .getConnectionAchievementsOverview()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.overview = response;
          this.buildSections(response);
          this.recentlyUnlocked = this.getRecentlyUnlocked(response);
          this.recentlyUnlockedIds = new Set(
            this.recentlyUnlocked.map(
              (item) => item.achievement?.id ?? item.id,
            ),
          );
          this.loading = false;
          // Trigger confetti only if the most recent achievement was earned today
          if (this.hasEarnedToday(response.earned_achievements)) {
            setTimeout(() => this.launchConfetti(), 400);
          }
        },
        error: (err) => {
          console.error('Error loading achievements:', err);
          this.error = true;
          this.errorMessage =
            'Unable to load achievements. Please try again later.';
          this.loading = false;
        },
      });
  }

  hasSectionPermission(eventType: number | string | null | undefined): boolean {
    if (eventType === null || eventType === undefined) return true;
    const typeNum = Number(eventType);
    if (typeNum === EventType.REFER_LEAD) {
      return this.authService.hasPermission(
        PermissionCode.CUSTOM_BUSINESS_RECORD,
      );
    }
    if (typeNum === EventType.ASSIGN_LEAD) {
      return this.authService.hasPermission(PermissionCode.GET_SET_LEADS);
    }
    return true;
  }

  private buildSections(
    response: AchievementsConnectionOverviewResponse,
  ): void {
    const progressMap = new Map<number, number>(
      response.progress.map((p: AchievementProgress) => [
        p.event_type,
        p.progress,
      ]),
    );

    const grouped = new Map<number, AchievementOverviewItem[]>();
    for (const achievement of response.all_achievements) {
      if (!grouped.has(achievement.event_type)) {
        grouped.set(achievement.event_type, []);
      }
      grouped.get(achievement.event_type)!.push(achievement);
    }

    const tierKeys: (keyof typeof BADGE_TIERS)[] = [
      'silver',
      'gold',
      'platinum',
      'diamond',
      'ruby',
      'sapphire',
    ];
    this.achievementTierMap.clear();

    this.sections = [];
    for (const [eventType, achievements] of grouped) {
      achievements.sort((a, b) => a.milestone - b.milestone);
      achievements.forEach((ach, index) => {
        const tierKey = tierKeys[Math.min(index, tierKeys.length - 1)];
        this.achievementTierMap.set(ach.id, BADGE_TIERS[tierKey]);
      });
      if (!this.hasSectionPermission(eventType)) {
        continue;
      }
      this.sections.push({
        eventType,
        label: this.EVENT_TYPE_LABELS[eventType] ?? `Event Type ${eventType}`,
        description:
          this.EVENT_TYPE_DESCRIPTIONS[eventType] ??
          'Complete milestones in this category to earn achievement badges.',
        achievements,
        progress: progressMap.get(eventType) ?? 0,
      });
    }
    this.sections.sort((a, b) => a.eventType - b.eventType);
  }

  private getRecentlyUnlocked(
    response: AchievementsConnectionOverviewResponse,
  ): AchievementConnection[] {
    const now = Date.now();
    const twoWeeksMs = this.RECENT_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;

    return [...response.earned_achievements]
      .filter((item) => {
        if (!item?.achieved_on) return false;
        if (
          item.achievement?.event_type &&
          !this.hasSectionPermission(item.achievement.event_type)
        ) {
          return false;
        }
        const achievedTime = new Date(item.achieved_on).getTime();
        return (
          !isNaN(achievedTime) &&
          now - achievedTime <= twoWeeksMs &&
          achievedTime <= now
        );
      })
      .sort(
        (a, b) =>
          new Date(b.achieved_on).getTime() - new Date(a.achieved_on).getTime(),
      )
      .slice(0, 5);
  }

  isSectionMastered(section: AchievementSection): boolean {
    if (!section?.achievements?.length) return false;
    return section.achievements.every((a) => a.earned);
  }

  celebrateSectionMastery(event: Event): void {
    event.stopPropagation();
    const target =
      (event.currentTarget as HTMLElement) || (event.target as HTMLElement);
    if (target) {
      this.burstConfettiOnElement(target, BADGE_TIERS['gold']);
    }
  }

  getSectionTheme(eventType: number | string) {
    const typeNum = Number(eventType);
    return (
      this.SECTION_THEMES[typeNum] ?? this.SECTION_THEMES[EventType.ASSIGN_LEAD]
    );
  }

  getSectionLabel(eventType: number | string): string {
    const typeNum = Number(eventType);
    return this.EVENT_TYPE_LABELS[typeNum] ?? `Category ${eventType}`;
  }

  getAchievementIconKey(achievement: AchievementOverviewItem): string {
    if (!achievement?.title) return 'default';
    return achievement.title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  isUnlockedToday(
    item:
      | AchievementConnection
      | AchievementOverviewItem
      | Achievement
      | string
      | null
      | undefined,
  ): boolean {
    if (!item) return false;
    let dateStr: string | null = null;
    if (typeof item === 'string') {
      dateStr = item;
    } else if ('achieved_on' in item && item.achieved_on) {
      dateStr = item.achieved_on;
    }
    if (!dateStr) return false;

    const earned = new Date(dateStr);
    if (isNaN(earned.getTime())) return false;
    const today = new Date();
    return (
      earned.getFullYear() === today.getFullYear() &&
      earned.getMonth() === today.getMonth() &&
      earned.getDate() === today.getDate()
    );
  }

  private hasEarnedToday(earnedAchievements: AchievementConnection[]): boolean {
    return (
      earnedAchievements?.some(
        (item) =>
          this.hasSectionPermission(item?.achievement?.event_type) &&
          this.isUnlockedToday(item),
      ) ?? false
    );
  }

  getThemeConfettiColors(eventType: number): string[] {
    const goldAndWhite = ['#fbbf24', '#f59e0b', '#ffffff', '#fef08a'];
    switch (Number(eventType)) {
      case EventType.REFER_LEAD:
        return [
          '#4f72d4',
          '#334ea5',
          '#6366f1',
          '#818cf8',
          '#a5b4fc',
          ...goldAndWhite,
        ];
      case EventType.ASSIGN_LEAD:
        return [
          '#06b6d4',
          '#0891b2',
          '#0284c7',
          '#38bdf8',
          '#67e8f9',
          ...goldAndWhite,
        ];
      case EventType.UPDATE_STATUS:
        return [
          '#f97316',
          '#ea580c',
          '#fb923c',
          '#fdba74',
          '#fed7aa',
          ...goldAndWhite,
        ];
      case EventType.FUND_LEAD:
        return [
          '#10b981',
          '#059669',
          '#047857',
          '#34d399',
          '#6ee7b7',
          ...goldAndWhite,
        ];
      case EventType.SHARE_LEAD:
        return [
          '#f43f5e',
          '#e11d48',
          '#be123c',
          '#fb7185',
          '#fda4af',
          ...goldAndWhite,
        ];
      default:
        return [
          '#334ea5',
          '#6b8fe8',
          '#fbbf24',
          '#34d399',
          '#f87171',
          '#a78bfa',
          '#ffffff',
        ];
    }
  }

  onRecentItemClick(item: AchievementConnection): void {
    if (!item?.achievement?.id) return;
    const achievementId = item.achievement.id;

    // Immediately highlight the clicked chip
    this.activeRecentAchievementId = achievementId;

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = null;
    }
    if (this.celebratingTimeout) {
      clearTimeout(this.celebratingTimeout);
      this.celebratingTimeout = null;
    }

    const card =
      (document.querySelector(
        `[data-achievement-id="${achievementId}"]`,
      ) as HTMLElement) ||
      document.getElementById(`achievement-card-${achievementId}`) ||
      document.getElementById('tour-achievement-card-0');
    if (card) {
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Delay confetti burst and card celebration until smooth scroll reaches the card
      this.scrollTimeout = setTimeout(() => {
        this.celebratingAchievementId = achievementId;
        this.burstConfettiOnElement(
          card,
          this.getAchievementTier(item.achievement),
        );

        this.celebratingTimeout = setTimeout(() => {
          this.celebratingAchievementId = null;
          this.activeRecentAchievementId = null;
        }, 2600);
      }, 600);
    }
  }

  openAchievementDetails(
    achievement: AchievementOverviewItem,
    section: AchievementSection,
  ): void {
    this.selectedAchievement = achievement;
    this.selectedSection = section;
  }

  closeAchievementDetails(): void {
    this.selectedAchievement = null;
    this.selectedSection = null;
  }

  navigateToSectionAction(eventType: number): void {
    if (!this.hasSectionPermission(eventType)) {
      return;
    }
    const urlMap: Record<
      number,
      { path: string; queryParams?: Record<string, string> }
    > = {
      [EventType.REFER_LEAD]: {
        path: '/leads/find',
        queryParams: { tab: 'custom', subtab: 'org' },
      },
      [EventType.ASSIGN_LEAD]: {
        path: '/leads/find',
        queryParams: { tab: 'deeproots', subtab: 'matched' },
      },
      [EventType.UPDATE_STATUS]: {
        path: '/leads/my-leads',
      },
      [EventType.FUND_LEAD]: {
        path: '/leads/my-leads',
      },
      [EventType.SHARE_LEAD]: {
        path: '/leads/find',
        queryParams: {
          tab: 'deeproots',
          subtab: 'inquiries',
          inquiry_tab: 'all',
        },
      },
    };

    const target = urlMap[eventType];
    if (target) {
      this.router.navigate([target.path], {
        queryParams: target.queryParams,
      });
    }
  }

  getSectionActionLabel(eventType: number): string {
    const labelMap: Record<number, string> = {
      [EventType.REFER_LEAD]: 'Refer a Business',
      [EventType.ASSIGN_LEAD]: 'Assign a Lead',
      [EventType.UPDATE_STATUS]: 'Update Lead Status',
      [EventType.FUND_LEAD]: 'Fund a Lead',
      [EventType.SHARE_LEAD]: 'Share a Lead',
    };
    return labelMap[eventType] ?? 'Go Earn More';
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closeAchievementDetails();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapePress(): void {
    if (this.selectedAchievement) {
      this.closeAchievementDetails();
    }
  }

  burstConfettiOnElement(
    element: HTMLElement,
    eventTypeOrTier?: number | string | BadgeTierInfo,
  ): void {
    const canvas = this.confettiCanvasRef?.nativeElement;
    if (!canvas) return;

    if (
      canvas.width !== window.innerWidth ||
      canvas.height !== window.innerHeight
    ) {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }

    const rect = element.getBoundingClientRect();
    const originX = rect.left + rect.width / 2;
    const originY = rect.top + Math.min(rect.height * 0.42, 60);

    let colors: string[];
    if (
      typeof eventTypeOrTier === 'object' &&
      eventTypeOrTier &&
      'confettiColors' in eventTypeOrTier
    ) {
      colors = eventTypeOrTier.confettiColors;
    } else if (
      typeof eventTypeOrTier === 'string' &&
      BADGE_TIERS[eventTypeOrTier]
    ) {
      colors = BADGE_TIERS[eventTypeOrTier].confettiColors;
    } else {
      colors = this.getThemeConfettiColors(Number(eventTypeOrTier) || 0);
    }

    const newParticles = this.spawnBurstParticles(originX, originY, colors, 75);

    this.activeParticles.push(...newParticles);
    this.startParticleAnimation();
  }

  private spawnBurstParticles(
    originX: number,
    originY: number,
    themeColors: string[],
    count = 75,
  ): ConfettiParticle[] {
    const shapes: ('rect' | 'circle' | 'star' | 'ribbon')[] = [
      'rect',
      'circle',
      'star',
      'ribbon',
    ];

    return Array.from({ length: count }, () => {
      const angle = Math.random() * Math.PI * 2;
      const speed = 3.5 + Math.random() * 8.5;
      const shape = shapes[Math.floor(Math.random() * shapes.length)];

      return {
        x: originX + (Math.random() - 0.5) * 24,
        y: originY + (Math.random() - 0.5) * 24,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 2.8,
        size: shape === 'star' ? 7 + Math.random() * 6 : 5 + Math.random() * 6,
        color: themeColors[Math.floor(Math.random() * themeColors.length)],
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 16,
        tiltAngle: Math.random() * Math.PI,
        tiltSpeed: (Math.random() - 0.5) * 0.25,
        shape,
        opacity: 1,
        life: 0,
        maxLife: 90 + Math.random() * 45,
        drag: 0.955,
        gravity: 0.16,
      };
    });
  }

  private spawnRainParticles(count = 110): ConfettiParticle[] {
    const canvas = this.confettiCanvasRef?.nativeElement;
    const width = canvas ? canvas.width : window.innerWidth;
    const colors = [
      '#334ea5',
      '#6b8fe8',
      '#fbbf24',
      '#34d399',
      '#f87171',
      '#a78bfa',
      '#fb923c',
      '#38bdf8',
      '#e879f9',
      '#4ade80',
      '#ffffff',
    ];
    const shapes: ('rect' | 'circle' | 'ribbon')[] = [
      'rect',
      'circle',
      'ribbon',
    ];

    return Array.from({ length: count }, () => ({
      x: Math.random() * width,
      y: -20 - Math.random() * 80,
      vx: (Math.random() - 0.5) * 3,
      vy: 2 + Math.random() * 4.5,
      size: 6 + Math.random() * 7,
      color: colors[Math.floor(Math.random() * colors.length)],
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 8,
      tiltAngle: Math.random() * Math.PI,
      tiltSpeed: (Math.random() - 0.5) * 0.15,
      shape: shapes[Math.floor(Math.random() * shapes.length)],
      opacity: 1,
      life: 0,
      maxLife: 180 + Math.random() * 40,
      drag: 0.99,
      gravity: 0.07,
    }));
  }

  private startParticleAnimation(): void {
    if (this.animationFrameId !== null) return;

    const canvas = this.confettiCanvasRef?.nativeElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const animate = () => {
      if (
        canvas.width !== window.innerWidth ||
        canvas.height !== window.innerHeight
      ) {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = this.activeParticles.length - 1; i >= 0; i--) {
        const p = this.activeParticles[i];

        p.vx *= p.drag;
        p.vy = p.vy * p.drag + p.gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.rotationSpeed;
        p.tiltAngle += p.tiltSpeed;
        p.life++;

        if (p.life > p.maxLife * 0.65) {
          const remaining = p.maxLife - p.life;
          const fadeDuration = p.maxLife * 0.35;
          p.opacity = Math.max(0, remaining / fadeDuration);
        }

        if (
          p.opacity <= 0 ||
          p.y > canvas.height + 50 ||
          p.x < -50 ||
          p.x > canvas.width + 50
        ) {
          this.activeParticles.splice(i, 1);
          continue;
        }

        ctx.save();
        ctx.globalAlpha = p.opacity;
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;

        const tilt = Math.sin(p.tiltAngle);

        if (p.shape === 'rect') {
          ctx.fillRect(
            -p.size / 2,
            (-p.size / 2) * tilt,
            p.size,
            p.size * tilt * 0.6 + 2,
          );
        } else if (p.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.shape === 'star') {
          ctx.beginPath();
          const s = p.size * 0.85;
          ctx.moveTo(0, -s);
          ctx.quadraticCurveTo(0, 0, s, 0);
          ctx.quadraticCurveTo(0, 0, 0, s);
          ctx.quadraticCurveTo(0, 0, -s, 0);
          ctx.quadraticCurveTo(0, 0, 0, -s);
          ctx.fill();
        } else {
          ctx.fillRect(
            -p.size * 0.8,
            -p.size * 0.25 * tilt,
            p.size * 1.6,
            p.size * 0.5 * tilt + 1,
          );
        }
        ctx.restore();
      }

      if (this.activeParticles.length > 0) {
        this.animationFrameId = requestAnimationFrame(animate);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        this.animationFrameId = null;
      }
    };

    this.ngZone.runOutsideAngular(() => {
      this.animationFrameId = requestAnimationFrame(animate);
    });
  }

  private launchConfetti(): void {
    const canvas = this.confettiCanvasRef?.nativeElement;
    if (!canvas) return;

    if (
      canvas.width !== window.innerWidth ||
      canvas.height !== window.innerHeight
    ) {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }

    const rain = this.spawnRainParticles(110);
    this.activeParticles.push(...rain);
    this.startParticleAnimation();
  }

  override ngOnDestroy(): void {
    if (this.celebratingTimeout) {
      clearTimeout(this.celebratingTimeout);
      this.celebratingTimeout = null;
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = null;
    }
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.activeParticles = [];
    super.ngOnDestroy();
  }

  isNextToEarn(
    section: AchievementSection,
    achievement: AchievementOverviewItem,
  ): boolean {
    if (achievement.earned) return false;
    const unearned = section.achievements.filter((a) => !a.earned);
    return unearned.length > 0 && unearned[0].id === achievement.id;
  }

  getProgressPercent(achievement: AchievementOverviewItem): number {
    if (!achievement.milestone) return 0;
    return Math.min(
      100,
      Math.round((achievement.current_progress / achievement.milestone) * 100),
    );
  }

  readonly isEarned = (a: AchievementOverviewItem): boolean => a.earned;

  trackByAchievement(
    _index: number,
    achievement: AchievementOverviewItem,
  ): number {
    return achievement.id;
  }

  trackBySection(_index: number, section: AchievementSection): number {
    return section.eventType;
  }

  trackByRecent(_index: number, item: AchievementConnection): number {
    return item.id;
  }

  isRecentlyUnlocked(
    achievement:
      | Achievement
      | AchievementOverviewItem
      | AchievementConnection
      | number
      | null
      | undefined,
  ): boolean {
    if (!achievement) return false;

    if (typeof achievement === 'number') {
      return this.recentlyUnlockedIds.has(achievement);
    }

    if ('achievement' in achievement && achievement.achievement?.id) {
      if (this.recentlyUnlockedIds.has(achievement.achievement.id)) {
        return true;
      }
      if (achievement.achieved_on) {
        return this.isWithinTwoWeeks(achievement.achieved_on);
      }
    }

    if ('id' in achievement && achievement.id) {
      if (this.recentlyUnlockedIds.has(achievement.id)) {
        return true;
      }
      if (
        'earned' in achievement &&
        achievement.earned &&
        achievement.achieved_on
      ) {
        return this.isWithinTwoWeeks(achievement.achieved_on);
      }
    }

    return false;
  }

  getAchievementTier(
    achievement:
      | Achievement
      | AchievementOverviewItem
      | AchievementConnection
      | number
      | null
      | undefined,
  ): BadgeTierInfo {
    if (!achievement) return BADGE_TIERS['silver'];
    const id =
      typeof achievement === 'number'
        ? achievement
        : 'achievement' in achievement && achievement.achievement?.id
          ? achievement.achievement.id
          : 'id' in achievement
            ? achievement.id
            : null;

    if (id && this.achievementTierMap.has(id)) {
      return this.achievementTierMap.get(id)!;
    }

    const milestone =
      typeof achievement === 'object' &&
      achievement &&
      'milestone' in achievement
        ? (achievement as AchievementOverviewItem).milestone
        : 1;

    if (milestone <= 1) return BADGE_TIERS['silver'];
    if (milestone <= 5) return BADGE_TIERS['gold'];
    if (milestone <= 15) return BADGE_TIERS['platinum'];
    if (milestone <= 20) return BADGE_TIERS['diamond'];
    if (milestone <= 30) return BADGE_TIERS['ruby'];
    return BADGE_TIERS['sapphire'];
  }

  private isWithinTwoWeeks(dateStr: string): boolean {
    if (!dateStr) return false;
    const achievedTime = new Date(dateStr).getTime();
    if (isNaN(achievedTime)) return false;
    const now = Date.now();
    const twoWeeksMs = this.RECENT_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;
    return now - achievedTime <= twoWeeksMs && achievedTime <= now;
  }
}
